/**
 * Extracts specific default values from the URL search parameters.
 * Uses getUrlParameters and filters the necessary fields.
 * @param {NextRouter} parameters The router reference
 * @returns {{
 *   adultsNumber?: string
 *   adultsPerRoom?: string;
 *   area?: string;
 *   childrenAges?: string[],
 *   childrenAgesPerRoom?: string;
 *   departureDate?: Date,
 *   departureId?: string,
 *   destinationIds?: string[],
 *   destinationRegionIds?: string[],
 *   durationRange?: string,
 *   durationType?: string,
 *   hotelIds?: string[],
 *   maxDepartureDate?: Date,
 * }} An object containing the filtered and parsed parameters.
 */
export const getSearchParameters = (
  parameters: URLSearchParams,
): {
  adultsNumber?: string;
  adultsPerRoom?: string;
  area?: string;
  childrenAges?: string[];
  childrenAgesPerRoom?: string;
  countryIds?: string[];
  departureDate?: Date;
  departureId?: string;
  destinationIds?: string[];
  destinationRegionIds?: string[];
  durationRange?: string;
  durationType?: string;
  hotelIds?: string[];
  maxDepartureDate?: Date;
} => {
  const getParameter = (keys: string[]): string | undefined => {
    const matchingKey = keys.find(key => parameters.get(key) !== null);

    return matchingKey ? (parameters.get(matchingKey) ?? undefined) : undefined;
  };

  const parseArray = (value?: string): string[] | undefined =>
    value ? value.split(',') : undefined;

  const parseDate = (value?: string): Date | undefined =>
    value ? new Date(value) : undefined;

  const adultsNumber = getParameter(['adultsNumber', 'AdultsNo']);
  const adultsPerRoom = getParameter([
    'adultsPerRoom',
    'adultsNoPerRoom',
    'AdultsNoPerRoom',
  ]);
  const area = getParameter(['area', 'Area']);
  const childrenAges = parseArray(
    getParameter(['childrenAges', 'ChildrenAges']),
  );
  const childrenAgesPerRoom = getParameter([
    'childrenAgesPerRoom',
    'ChildrenAgesPerRoom',
  ]);
  const departureDate = parseDate(
    getParameter(['departureDate', 'DepartureDate']),
  );
  const countryIds = parseArray(getParameter(['countryIds', 'CountryIds']));
  const departureId = getParameter(['departureIds', 'DepartureId']);
  const destinationIds = parseArray(
    getParameter(['destinationIds', 'DestinationIds']),
  );
  const destinationRegionIds = parseArray(
    getParameter(['destinationRegionIds', 'DestinationRegionIds']),
  );
  const durationRange = getParameter(['durationRange', 'DurationRange']);
  const durationType = getParameter(['durationType', 'DurationType']);
  const hotelIds = parseArray(getParameter(['hotelIds', 'HotelIds']));
  const maxDepartureDate = parseDate(
    getParameter(['maxDepartureDate', 'MaxDepartureDate']),
  );

  return {
    adultsNumber,
    adultsPerRoom,
    area,
    childrenAges,
    childrenAgesPerRoom,
    countryIds,
    departureDate,
    departureId,
    destinationIds,
    destinationRegionIds,
    durationRange,
    durationType,
    hotelIds,
    maxDepartureDate,
  };
};
